/**
 * 长按手势的判定：长按视频中间弹菜单，长按两侧顶倍速。
 *
 * 规则和数字都照红果短剧 6.8.5.32 的实现。
 * 和 swipe.ts 一样单独拿出来：按多久算长按、按在哪个区域、顶速期间
 * 滑多远算锁定/退出/拖进度，这几个数字抽成纯函数才能脱离 DOM 去测。
 */

/** 按住这么久不松手，才算长按。红果用的是安卓系统默认值。 */
export const LONG_PRESS_MS = 400

/** 中间区占屏宽的比例，水平居中。红果是 33%，正中偏左一点就已经是顶速区了。 */
const MIDDLE_RATIO = 0.33

export type PressZone = 'left' | 'middle' | 'right'

/**
 * 按下的点落在视频区域的哪一段。
 *
 * 按比例算，不按像素算——和视频在不同机型上的实际渲染尺寸无关。
 * 精确落在分界线上的触点算中间。
 */
export function zoneFor(clientX: number, rect: { left: number; width: number }): PressZone {
  if (rect.width <= 0) return 'middle'
  const ratio = (clientX - rect.left) / rect.width
  const sideRatio = (1 - MIDDLE_RATIO) / 2
  if (ratio < sideRatio) return 'left'
  if (ratio > 1 - sideRatio) return 'right'
  return 'middle'
}

/** 长按两侧顶到的倍速，不管当前挂着几倍——不是「当前倍速再加倍」。 */
export const BOOST_RATE = 2

/** 顶速期间手指要走这么远，才算上滑锁定、下滑退出、横拖进度。红果是 50dp。 */
export const BOOST_DRAG_PX = 50

/**
 * 顶速期间这一次按压走到了哪一步，提示条的文案跟着它变。
 *
 * - `hold`：按住顶速中。未锁定时提示「上滑锁定」，已锁定时提示「下滑退出」
 * - `lockArmed`：上滑够了，松手就锁定
 * - `unlockArmed`：已锁定，下滑够了，松手就退出
 * - `seeking`：横向拖进度。这一次按压不再管锁定，倍速照旧
 */
export type BoostStep = 'hold' | 'lockArmed' | 'unlockArmed' | 'seeking'

/**
 * dx/dy 从长按触发那一刻的落点算起（向上为负）。
 *
 * 方向按主轴定：横向占优且够远就进拖进度；一旦进了拖进度、或者竖向已经武装，
 * 这一次按压就不再换方向。未锁定时往下滑什么也不做——红果没有「下滑取消顶速」，
 * 松手自然恢复原速。武装之后手指退回起点另一侧才撤销武装。
 */
export function nextBoostStep(step: BoostStep, dx: number, dy: number, locked: boolean): BoostStep {
  if (step === 'seeking') return 'seeking'
  if (step === 'hold' && Math.abs(dx) > Math.abs(dy)) {
    return Math.abs(dx) >= BOOST_DRAG_PX ? 'seeking' : 'hold'
  }
  if (locked) {
    if (dy >= BOOST_DRAG_PX) return 'unlockArmed'
    return dy < 0 ? 'hold' : step
  }
  if (dy <= -BOOST_DRAG_PX) return 'lockArmed'
  return dy > 0 ? 'hold' : step
}

/**
 * 横拖进度的目标时间：手指横移整条进度条那么宽，就是整集时长。
 * dx 从开始拖的那一刻算，不把进入拖动前那 50px 也算进去——否则一开拖画面就先跳一截。
 */
export function seekTarget(startTime: number, dx: number, trackWidth: number, duration: number): number {
  if (trackWidth <= 0 || duration <= 0) return startTime
  return Math.min(duration, Math.max(0, startTime + (dx / trackWidth) * duration))
}
